import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  createSocleCategory,
  createSocleSubCategory,
  listSocleCategories,
  listSocleSubCategories,
  setSocleCategoryStatus,
  setSocleSubCategoryStatus,
  updateSocleCategory,
  type CategoryKind,
  type SocleCategory,
  type SocleSubCategory,
} from '@/lib/coreSocle';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

interface FormState {
  id: number | null;
  nom: string;
  description: string;
  slug: string;
  parentId: number | null;
}

const EMPTY: FormState = { id: null, nom: '', description: '', slug: '', parentId: null };

export default function CategoriesTab({ commerceId, org }: Props) {
  const [kind, setKind] = useState<CategoryKind>('product');
  const [cats, setCats] = useState<SocleCategory[]>([]);
  const [subs, setSubs] = useState<SocleSubCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [subForParent, setSubForParent] = useState<SocleCategory | null>(null);
  const [subName, setSubName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [c, s] = await Promise.all([
        listSocleCategories(commerceId, kind),
        listSocleSubCategories(commerceId),
      ]);
      setCats(c);
      setSubs(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [commerceId, kind]);

  useEffect(() => { load(); }, [load]);

  const subsOf = useMemo(() => (id: number) => subs.filter((s) => s.idcategorie === id), [subs]);
  const roots = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);
  const childrenOf = useMemo(() => (id: number) => cats.filter((c) => c.parent_id === id), [cats]);

  const openCreate = (parent?: SocleCategory | null) => {
    setForm({ ...EMPTY, parentId: parent ? parent.id : null });
    setOpen(true);
  };

  const openEdit = (c: SocleCategory) => {
    setForm({ id: c.id, nom: c.nom, description: c.description || '', slug: c.slug || '', parentId: c.parent_id });
    setOpen(true);
  };

  const save = async () => {
    if (!form.nom.trim()) return;
    setSaving(true);
    try {
      if (form.id) {
        await updateSocleCategory(form.id, kind, {
          nom: form.nom.trim(),
          description: form.description.trim() || null,
          slug: form.slug.trim() || form.nom,
          parentId: form.parentId,
        });
      } else {
        await createSocleCategory({
          commerceId,
          kind,
          nom: form.nom.trim(),
          description: form.description.trim() || null,
          parentId: form.parentId,
        });
      }
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (c: SocleCategory) => {
    setBusyId(c.id);
    try {
      await setSocleCategoryStatus(c.id, kind, c.status === 'inactive' ? 'active' : 'inactive');
      load();
    } finally {
      setBusyId(null);
    }
  };

  const addSub = async () => {
    if (!subForParent || !subName.trim()) return;
    setSaving(true);
    try {
      await createSocleSubCategory({
        commerceId,
        parentId: subForParent.id,
        parentName: subForParent.nom,
        nom: subName.trim(),
      });
      setSubForParent(null);
      setSubName('');
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleSub = async (s: SocleSubCategory) => {
    setBusyId(s.id);
    try {
      await setSocleSubCategoryStatus(s.id, s.status === 'inactive' ? 'active' : 'inactive');
      load();
    } finally {
      setBusyId(null);
    }
  };

  const renderChildren = (parent: SocleCategory) => {
    const kids = childrenOf(parent.id);
    const sc = subsOf(parent.id);
    if (kids.length === 0 && sc.length === 0) return null;
    return (
      <div className="ml-6 mt-2 space-y-1.5 border-l border-background-200/70 pl-4">
        {kids.map((k) => <CategoryRow key={`c${k.id}`} cat={k} level={2} onEdit={openEdit} onToggle={toggleStatus} onAddSub={setSubForParent} busyId={busyId} subsOf={subsOf} onToggleSub={toggleSub} />)}
        {sc.map((s) => (
          <div key={`s${s.id}`} className={`flex items-center justify-between gap-2 px-3 py-1.5 rounded-md bg-background-100/60 ${s.status === 'inactive' ? 'opacity-60' : ''}`}>
            <span className="flex items-center gap-2 text-xs text-foreground-600 min-w-0">
              <i className="ri-subtract-line text-foreground-400"></i>
              <span className="truncate">{s.nom}</span>
              {s.slug && <span className="text-[10px] text-foreground-400 font-mono hidden sm:inline">/{s.slug}</span>}
            </span>
            <button onClick={() => toggleSub(s)} disabled={busyId === s.id} className="text-[10px] px-2 py-0.5 rounded-full bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-800 cursor-pointer whitespace-nowrap disabled:opacity-50">
              {s.status === 'inactive' ? 'Réactiver' : 'Désactiver'}
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          {([['product', 'Produits', 'ri-shopping-bag-line'], ['service', 'Services', 'ri-service-line']] as const).map(([k, label, icon]) => (
            <button
              key={k}
              onClick={() => setKind(k as CategoryKind)}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${kind === k ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
            >
              <i className={icon}></i>{label}
            </button>
          ))}
        </div>
        <button
          onClick={() => openCreate(null)}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-add-line"></i> Nouvelle catégorie
        </button>
      </div>

      <p className="text-xs text-foreground-500 mb-4">
        Système de catégories réutilisable — {kind === 'product' ? 'produits' : 'services'}. Hiérarchie, slug et statut partagés par tous les modules.
      </p>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : roots.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-price-tag-3-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucune catégorie {kind === 'product' ? 'produit' : 'service'}.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {roots.map((c) => (
            <div key={c.id} className="bg-background-50 border border-background-200/70 rounded-lg p-3">
              <CategoryRow cat={c} level={1} onEdit={openEdit} onToggle={toggleStatus} onAddSub={setSubForParent} busyId={busyId} subsOf={subsOf} onToggleSub={toggleSub} />
              {renderChildren(c)}
            </div>
          ))}
        </div>
      )}

      {open && (
        <Modal
          title={form.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
          onClose={() => setOpen(false)}
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={save} disabled={saving || !form.nom.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Nom</span>
              <input className={INPUT} value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Ex : Boissons" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Description</span>
              <textarea className={`${INPUT} min-h-[70px]`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Catégorie parente (hiérarchie)</span>
              <select className={INPUT} value={form.parentId ?? ''} onChange={(e) => setForm({ ...form, parentId: e.target.value ? Number(e.target.value) : null })}>
                <option value="">Aucune (catégorie racine)</option>
                {cats.filter((c) => c.id !== form.id).map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Slug (URL)</span>
              <input className={INPUT} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="boissons" />
            </label>
          </div>
        </Modal>
      )}

      {subForParent && (
        <Modal
          title={`Sous-catégorie de « ${subForParent.nom} »`}
          onClose={() => { setSubForParent(null); setSubName(''); }}
          footer={
            <>
              <button onClick={() => { setSubForParent(null); setSubName(''); }} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={addSub} disabled={saving || !subName.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50">
                Ajouter
              </button>
            </>
          }
        >
          <label className="block">
            <span className="block text-xs font-medium text-foreground-600 mb-1">Nom de la sous-catégorie</span>
            <input className={INPUT} value={subName} onChange={(e) => setSubName(e.target.value)} placeholder="Ex : Jus frais" autoFocus />
          </label>
        </Modal>
      )}
    </div>
  );
}

function CategoryRow({
  cat, level, onEdit, onToggle, onAddSub, busyId, subsOf, onToggleSub,
}: {
  cat: SocleCategory;
  level: number;
  onEdit: (c: SocleCategory) => void;
  onToggle: (c: SocleCategory) => void;
  onAddSub: (c: SocleCategory) => void;
  busyId: number | null;
  subsOf: (id: number) => SocleSubCategory[];
  onToggleSub: (s: SocleSubCategory) => void;
}) {
  const inactive = cat.status === 'inactive';
  const subCount = subsOf(cat.id).length;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        {level === 1 ? <i className="ri-folder-2-line text-primary-500"></i> : <i className="ri-folders-line text-secondary-500"></i>}
        <span className={`text-sm font-semibold truncate ${inactive ? 'text-foreground-400 line-through' : 'text-foreground-900'}`}>{cat.nom}</span>
        {cat.slug && <span className="text-[10px] text-foreground-400 font-mono hidden sm:inline">/{cat.slug}</span>}
        {subCount > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-medium whitespace-nowrap">{subCount} sous-cat.</span>}
        {inactive && <span className="text-[10px] px-2 py-0.5 rounded-full bg-foreground-100 text-foreground-500 whitespace-nowrap">Désactivée</span>}
      </div>
      <div className="flex items-center gap-1.5">
        <button onClick={() => onAddSub(cat)} className="text-[11px] px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 cursor-pointer whitespace-nowrap transition-colors">
          <i className="ri-add-line mr-0.5"></i>Sous-cat.
        </button>
        <button onClick={() => onEdit(cat)} className="text-[11px] px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 cursor-pointer whitespace-nowrap transition-colors">
          <i className="ri-edit-line mr-0.5"></i>Modifier
        </button>
        <button onClick={() => onToggle(cat)} disabled={busyId === cat.id} className={`text-[11px] px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 ${inactive ? 'bg-accent-100 text-accent-900' : 'bg-background-100 text-foreground-500 hover:bg-background-200/70'}`}>
          {inactive ? 'Réactiver' : 'Désactiver'}
        </button>
      </div>
    </div>
  );
}